// src/pages/SuperAdmin/Restaurants.jsx - WITH MANDATORY FIELDS & EMAIL VALIDATION

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Plus, Edit, Trash2, X, Loader, Search, Building2, MapPin, Phone, Download, Calendar, AlertCircle, Clock, CheckCircle } from 'lucide-react';
import StorageService from '../../services/StorageService';
import PasswordService from '../../services/PasswordService';
import SubscriptionService from '../../services/SubscriptionService';
import { doc, setDoc, deleteDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebaseConfig';
import * as XLSX from 'xlsx';
import AuthService from '../../services/AuthService';

const SuperAdminRestaurants = () => {
  const [restaurants, setRestaurants] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingRestaurant, setEditingRestaurant] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    username: '',
    password: '',
    package: 'Basic',
    status: 'Active',
    city: '',
    state: '',
    country: '',
    address: '',
    gstNo: '',
    ownerPhone: '',
    email: '',
    pinCode: '',
    subscriptionMonths: 1,
    subscriptionEndDate: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [expiringRestaurants, setExpiringRestaurants] = useState([]);
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailExists, setEmailExists] = useState(false);
  const [emailCheckMessage, setEmailCheckMessage] = useState('');
  const [availablePackages, setAvailablePackages] = useState([]);


  useEffect(() => {
    loadRestaurants();
    loadPackages();
    checkSubscriptions();
    
    // Check subscriptions every hour
    const interval = setInterval(checkSubscriptions, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

   const loadPackages = async () => {
    try {
      const data = await StorageService.getData();
      setAvailablePackages(data.packages || []);
    } catch (error) {
      console.error('❌ Error loading packages:', error);
      setAvailablePackages([]);
    }
  };

  const loadRestaurants = async () => {
    try {
      setLoading(true);
       
      const data = await StorageService.getData();
       
      setRestaurants(data.restaurants || []);
    } catch (error) {
      console.error('❌ Error loading restaurants:', error);
      setRestaurants([]);
    } finally {
      setLoading(false);
    }
  };



  const checkSubscriptions = async () => {
    try {
       
      
      // Auto-deactivate expired subscriptions
      const result = await SubscriptionService.checkAndDeactivateExpiredSubscriptions();
      
      if (result.deactivatedCount > 0) {
         
        await loadRestaurants(); // Reload to show updated status
      }
      
      // Get restaurants expiring soon
      const expiring = await SubscriptionService.getExpiringSoonRestaurants();
      setExpiringRestaurants(expiring);
      
      if (expiring.length > 0) {
         
      }
    } catch (error) {
      console.error('❌ Error checking subscriptions:', error);
    }
  };

  // ✅ CHECK IF EMAIL EXISTS IN FIREBASE
  const checkEmailExists = async (email) => {
    if (!email || !email.includes('@')) {
      setEmailExists(false);
      setEmailCheckMessage('');
      return false;
    }

    try {
      setEmailChecking(true);
       

      // Query Firestore for existing restaurant with this email
      const restaurantsRef = collection(db, 'restaurants');
      const q = query(restaurantsRef, where('email', '==', email.toLowerCase().trim()));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        // Email exists
        const existingRestaurant = querySnapshot.docs[0].data();
         
        setEmailExists(true);
        setEmailCheckMessage(`❌ This email is already registered to "${existingRestaurant.name}"`);
        return true;
      } else {
        // Email available
         
        setEmailExists(false);
        setEmailCheckMessage('✅ Email is available');
        return false;
      }
    } catch (error) {
      console.error('❌ Error checking email:', error);
      setEmailCheckMessage('⚠️ Could not verify email. Please try again.');
      return false;
    } finally {
      setEmailChecking(false);
    }
  };

  const filteredRestaurants = useMemo(() => {
    if (!searchTerm.trim()) return restaurants;

    const term = searchTerm.toLowerCase();
    return restaurants.filter(restaurant => 
      restaurant.name.toLowerCase().includes(term) ||
      restaurant.username.toLowerCase().includes(term) ||
      restaurant.package.toLowerCase().includes(term) ||
      (restaurant.city && restaurant.city.toLowerCase().includes(term)) ||
      (restaurant.state && restaurant.state.toLowerCase().includes(term))
    );
  }, [restaurants, searchTerm]);

  const handleExportToExcel = useCallback(() => {
    try {
      setExporting(true);
       

      const exportData = filteredRestaurants.map(restaurant => ({
        'Restaurant Name': restaurant.name || '',
        'Username': restaurant.username || '',
        'Email': restaurant.email || '',
        'Package': restaurant.package || '',
        'Status': restaurant.status || '',
        'Subscription End Date': restaurant.subscriptionEndDate 
          ? new Date(restaurant.subscriptionEndDate).toLocaleDateString() 
          : 'Not Set',
        'Days Remaining': restaurant.subscriptionEndDate 
          ? SubscriptionService.getDaysRemaining(restaurant.subscriptionEndDate)
          : 'N/A',
        'City': restaurant.city || '',
        'State': restaurant.state || '',
        'Country': restaurant.country || '',
        'Pin Code': restaurant.pinCode || '',
        'Address': restaurant.address || '',
        'GST Number': restaurant.gstNo || '',
        'Owner Phone': restaurant.ownerPhone || '',
        'Menu Items': restaurant.menu?.length || 0,
        'Tables': restaurant.tables?.length || 0,
        'Created At': restaurant.createdAt ? new Date(restaurant.createdAt).toLocaleDateString() : ''
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);

      const columnWidths = [
        { wch: 25 }, { wch: 15 }, { wch: 25 }, { wch: 12 }, { wch: 10 }, 
        { wch: 18 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 },
        { wch: 10 }, { wch: 40 }, { wch: 20 }, { wch: 15 }, { wch: 12 }, 
        { wch: 10 }, { wch: 15 }
      ];
      worksheet['!cols'] = columnWidths;

      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Restaurants');

      const date = new Date().toISOString().split('T')[0];
      const filename = `Restaurants_${date}.xlsx`;

      XLSX.writeFile(workbook, filename);

       
      alert(`✅ Excel file "${filename}" downloaded successfully!`);
    } catch (error) {
      console.error('❌ Error exporting to Excel:', error);
      alert('Failed to export Excel file. Please try again.');
    } finally {
      setExporting(false);
    }
  }, [filteredRestaurants]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ VALIDATE ALL MANDATORY FIELDS
    const requiredFields = {
      name: 'Restaurant Name',
      username: 'Username',
      email: 'Email Address',
      password: editingRestaurant ? null : 'Password',
      package: 'Package',
      status: 'Status',
      ownerPhone: 'Owner Phone Number',
      city: 'City',
      state: 'State',
      country: 'Country',
      address: 'Full Address',
      pinCode: 'Pin Code',
      subscriptionMonths: 'Subscription Duration'
    };

    for (const [field, label] of Object.entries(requiredFields)) {
      if (label && !formData[field]?.toString().trim()) {
        alert(`❌ ${label} is required!`);
        return;
      }
    }

    // ✅ VALIDATE EMAIL FORMAT
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      alert('❌ Please enter a valid email address!');
      return;
    }

    // ✅ VALIDATE PASSWORD (only for new restaurants)
    if (!editingRestaurant && formData.password.length < 6) {
      alert('❌ Password must be at least 6 characters long!');
      return;
    }

    // ✅ VALIDATE PHONE NUMBER
    const phoneRegex = /^[0-9+\s()-]{10,15}$/;
    if (!phoneRegex.test(formData.ownerPhone)) {
      alert('❌ Please enter a valid phone number (10-15 digits)!');
      return;
    }

    // ✅ VALIDATE PIN CODE
    const pinCodeRegex = /^[0-9]{5,6}$/;
    if (!pinCodeRegex.test(formData.pinCode)) {
      alert('❌ Please enter a valid pin code (5-6 digits)!');
      return;
    }

    // ✅ CHECK IF EMAIL EXISTS (only for new restaurants)
    if (!editingRestaurant) {
      const exists = await checkEmailExists(formData.email);
      if (exists) {
        alert('❌ This email is already registered! Please use a different email.');
        return;
      }
    }

    try {
      setSaving(true);

      if (editingRestaurant) {
        // ==================== EDITING EXISTING RESTAURANT ====================
        const restaurantId = editingRestaurant.id;
        
        // Calculate new subscription end date if months changed
        let subscriptionEndDate = editingRestaurant.subscriptionEndDate;
        if (formData.subscriptionMonths) {
          subscriptionEndDate = SubscriptionService.calculateEndDate(new Date(), parseInt(formData.subscriptionMonths));
        }

        // Prepare update data
        const updateData = {
          name: formData.name,
          username: formData.username,
          email: formData.email,
          package: formData.package,
          status: formData.status,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          address: formData.address,
          pinCode: formData.pinCode,
          gstNo: formData.gstNo,
          ownerPhone: formData.ownerPhone,
          subscriptionEndDate: subscriptionEndDate,
          updatedAt: new Date().toISOString()
        };

        // Update UI immediately (optimistic update)
        const updatedRestaurant = {
          ...editingRestaurant,
          ...updateData
        };

        setRestaurants(prevRestaurants => 
          prevRestaurants.map(r => 
            r.id === restaurantId ? updatedRestaurant : r
          )
        );

        // Close modal immediately
        setShowModal(false);
        setEditingRestaurant(null);
        resetForm();
        setSaving(false);

        alert('✅ Restaurant updated! Saving to database...');

        // Save to Firestore in background
        const restaurantRef = doc(db, 'restaurants', restaurantId);
        setDoc(restaurantRef, updateData, { merge: true })
          .then(() => {
             
            StorageService.clearCache();
            checkSubscriptions();
          })
          .catch((error) => {
            console.error('❌ Error saving to Firestore:', error);
            alert('⚠️ Changes may not be saved. Please refresh and try again.');
            loadRestaurants();
          });

      } else {
        // ==================== CREATING NEW RESTAURANT ====================
        
         

        // Step 1: Create Firebase Auth user
         
        
        const authResult = await AuthService.createUser(
          formData.email,
          formData.password,
          {
            name: formData.name,
            role: 'restaurant_owner'
          }
        );

        if (!authResult.success) {
          alert(`❌ Failed to create user account: ${authResult.message}`);
          setSaving(false);
          return;
        }

         

        // Step 2: Calculate subscription end date
        const subscriptionEndDate = SubscriptionService.calculateEndDate(
          new Date(), 
          parseInt(formData.subscriptionMonths)
        );

        // Step 3: Create restaurant document ID
        const newRestaurantId = `rest_${Date.now()}`;
        
         

        // Step 4: Create restaurant document data
        const restaurantData = {
          id: newRestaurantId,
          firebaseUid: authResult.uid,
          name: formData.name,
          username: formData.username,
          email: formData.email.toLowerCase().trim(),
          package: formData.package,
          status: formData.status,
          city: formData.city,
          state: formData.state,
          country: formData.country,
          address: formData.address,
          pinCode: formData.pinCode,
          gstNo: formData.gstNo,
          ownerPhone: formData.ownerPhone,
          subscriptionEndDate,
          categories: ['Main Course', 'Starters', 'Desserts', 'Beverages'],
          subcategories: ['Veg', 'Non-Veg', 'Vegan'],
          foodOfTheDay: null,
          createdAt: new Date().toISOString(),
          authProvider: 'firebase'
        };

        // ✅ CRITICAL: Save to Firestore FIRST (before updating UI)
        const restaurantRef = doc(db, 'restaurants', newRestaurantId);
        
        try {
          await setDoc(restaurantRef, restaurantData);
           
        } catch (firestoreError) {
          console.error('❌ Error saving to Firestore:', firestoreError);
          alert(`❌ Failed to save restaurant: ${firestoreError.message}`);
          setSaving(false);
          return;
        }

        // Step 5: Update UI after successful Firestore save
        const newRestaurant = {
          ...restaurantData,
          menu: [],
          orders: [],
          tables: []
        };

        setRestaurants(prevRestaurants => [...prevRestaurants, newRestaurant]);

        // Step 6: Clear cache to force fresh data on next load
        StorageService.clearCache();

        // Step 7: Close modal and reset form
        setShowModal(false);
        setEditingRestaurant(null);
        resetForm();
        setSaving(false);

        // Step 8: Show success message
        alert(
          `✅ Restaurant created successfully!\n\n` +
          `📧 Verification email sent to: ${formData.email}\n` +
          `The restaurant owner must verify their email before logging in.\n\n` +
          `Login Credentials:\n` +
          `Email: ${formData.email}\n` +
          `Password: (as set)`
        );

        // Step 9: Reload restaurants to ensure sync
        await loadRestaurants();
        await checkSubscriptions();
        
         
      }

    } catch (error) {
      console.error('❌ Error saving restaurant:', error);
      alert(`Failed to save restaurant: ${error.message}`);
      setSaving(false);
    }
  };

  const handleEdit = useCallback((restaurant) => {
    setEditingRestaurant(restaurant);
    setFormData({
      name: restaurant.name,
      username: restaurant.username,
      password: restaurant.password,
      package: restaurant.package,
      status: restaurant.status,
      city: restaurant.city || '',
      state: restaurant.state || '',
      country: restaurant.country || '',
      address: restaurant.address || '',
      pinCode: restaurant.pinCode || '',
      gstNo: restaurant.gstNo || '',
      ownerPhone: restaurant.ownerPhone || '',
      email: restaurant.email || '',
      subscriptionMonths: 1,
      subscriptionEndDate: restaurant.subscriptionEndDate || ''
    });
    setEmailExists(false);
    setEmailCheckMessage('');
    setShowModal(true);
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this restaurant? This will delete all menu items, orders, and tables.')) {
      try {
         

        // Update UI immediately
        setRestaurants(prevRestaurants => prevRestaurants.filter(r => r.id !== id));
        
        alert('✅ Restaurant deleted! Removing from database...');

        // Delete from Firestore in background
        const restaurantRef = doc(db, 'restaurants', id);
        deleteDoc(restaurantRef)
          .then(() => {
             
            StorageService.clearCache();
          })
          .catch((error) => {
            console.error('❌ Error deleting from Firestore:', error);
            alert('⚠️ Deletion may have failed. Please refresh and try again.');
            loadRestaurants();
          });

      } catch (error) {
        console.error('❌ Error deleting restaurant:', error);
        alert('Failed to delete restaurant. Please try again.');
        await loadRestaurants();
      }
    }
  };

  const handleFormChange = useCallback((field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // ✅ Auto-check email when it changes (only for new restaurants)
    if (field === 'email' && !editingRestaurant) {
      // Debounce email check
      const timeoutId = setTimeout(() => {
        checkEmailExists(value);
      }, 500);
      return () => clearTimeout(timeoutId);
    }
  }, [editingRestaurant]);

  const resetForm = () => {
    setFormData({
      name: '',
      username: '',
      password: '',
    package: availablePackages.length > 0 ? availablePackages[0].name : '',  // ← Changed
      status: 'Active',
      city: '',
      state: '',
      country: '',
      address: '',
      pinCode: '',
      gstNo: '',
      ownerPhone: '',
      email: '',
      subscriptionMonths: 1,
      subscriptionEndDate: ''
    });
    setEmailExists(false);
    setEmailCheckMessage('');
  };

  const closeModal = useCallback(() => {
    setShowModal(false);
    setEditingRestaurant(null);
    resetForm();
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const getSubscriptionStatus = (restaurant) => {
    if (!restaurant.subscriptionEndDate) {
      return { status: 'none', text: 'Not Set', color: 'gray' };
    }

    const daysRemaining = SubscriptionService.getDaysRemaining(restaurant.subscriptionEndDate);
    
    if (daysRemaining < 0) {
      return { status: 'expired', text: 'Expired', color: 'red', days: daysRemaining };
    } else if (daysRemaining <= 5) {
      return { status: 'expiring', text: `${daysRemaining} days left`, color: 'orange', days: daysRemaining };
    } else {
      return { status: 'active', text: `${daysRemaining} days left`, color: 'green', days: daysRemaining };
    }
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Loader className="w-12 h-12 text-orange-500 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading restaurants...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6">
      {/* Expiring Soon Alert */}
      {expiringRestaurants.length > 0 && (
        <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-6 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-orange-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-orange-800 mb-1">Subscriptions Expiring Soon</h3>
              <p className="text-sm text-orange-700 mb-2">
                {expiringRestaurants.length} restaurant{expiringRestaurants.length > 1 ? 's' : ''} will expire within 5 days:
              </p>
              <ul className="text-sm text-orange-700 space-y-1">
                {expiringRestaurants.map(r => (
                  <li key={r.id}>
                    • <strong>{r.name}</strong> - {r.daysRemaining} day{r.daysRemaining !== 1 ? 's' : ''} remaining
                    ({new Date(r.subscriptionEndDate).toLocaleDateString()})
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6 md:mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-800">Restaurants</h1>
        <div className="flex gap-2 md:gap-3">
          {restaurants.length > 0 && (
            <button
              onClick={handleExportToExcel}
              disabled={saving || exporting}
              className="bg-green-500 text-white px-4 md:px-6 py-2 md:py-3 rounded-lg font-semibold hover:bg-green-600 flex items-center gap-2 disabled:opacity-50 transition text-sm md:text-base"
              title="Export to Excel"
            >
              {exporting ? (
                <>
                  <Loader className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
                  <span className="hidden sm:inline">Exporting...</span>
                </>
              ) : (
                <>
                  <Download className="w-4 h-4 md:w-5 md:h-5" />
                  <span className="hidden sm:inline">Export Excel</span>
                </>
              )}
            </button>
          )}
          <button
            onClick={() => setShowModal(true)}
            disabled={saving}
            className="bg-orange-500 text-white px-4 md:px-6 py-2 md:py-3 rounded-lg font-semibold hover:bg-orange-600 flex items-center gap-2 disabled:opacity-50 transition text-sm md:text-base"
          >
            <Plus className="w-4 h-4 md:w-5 md:h-5" /> Add Restaurant
          </button>
        </div>
      </div>

      {restaurants.length === 0 ? (
        <div className="bg-white rounded-xl shadow-md p-8 md:p-12 text-center">
          <p className="text-gray-500 mb-4">No restaurants found</p>
          <button
            onClick={() => setShowModal(true)}
            className="bg-orange-500 text-white px-6 py-3 rounded-lg hover:bg-orange-600"
          >
            Create Your First Restaurant
          </button>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-xl shadow-md p-3 md:p-4 mb-4 md:mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-2.5 md:top-3 text-gray-400 w-4 h-4 md:w-5 md:h-5" />
              <input
                type="text"
                placeholder="Search restaurants..."
                value={searchTerm}
                onChange={handleSearchChange}
                className="w-full pl-9 md:pl-10 pr-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px]">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-gray-600">Name</th>
                    <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-gray-600">Username</th>
                    <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-gray-600">Location</th>
                    <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-gray-600">Contact</th>
                    <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-gray-600">Package</th>
                    <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-gray-600">Subscription</th>
                    <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-gray-600">Status</th>
                    <th className="px-4 md:px-6 py-3 md:py-4 text-left text-xs md:text-sm font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {filteredRestaurants.length > 0 ? (
                    filteredRestaurants.map(restaurant => {
                      const subStatus = getSubscriptionStatus(restaurant);
                      return (
                        <tr key={restaurant.id} className="hover:bg-gray-50 transition">
                          <td className="px-4 md:px-6 py-3 md:py-4 text-gray-800 font-medium text-sm md:text-base">{restaurant.name}</td>
                          <td className="px-4 md:px-6 py-3 md:py-4 text-gray-600 text-sm md:text-base">{restaurant.username}</td>
                          <td className="px-4 md:px-6 py-3 md:py-4 text-gray-600 text-sm">
                            {restaurant.city && restaurant.state ? (
                              <div className="flex items-center gap-1">
                                <MapPin className="w-3 h-3 md:w-4 md:h-4 text-gray-400 flex-shrink-0" />
                                <span>{restaurant.city}, {restaurant.state}</span>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs md:text-sm">Not set</span>
                            )}
                          </td>
                          <td className="px-4 md:px-6 py-3 md:py-4 text-gray-600 text-sm">
                            {restaurant.ownerPhone ? (
                              <div className="flex items-center gap-1">
                                <Phone className="w-3 h-3 md:w-4 md:h-4 text-gray-400 flex-shrink-0" />
                                <span>{restaurant.ownerPhone}</span>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-xs md:text-sm">Not set</span>
                            )}
                          </td>
                          <td className="px-4 md:px-6 py-3 md:py-4">
                            <span className="px-2 md:px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-xs md:text-sm font-medium">
                              {restaurant.package}
                            </span>
                          </td>
                          <td className="px-4 md:px-6 py-3 md:py-4">
                            <div className="flex flex-col gap-1">
                              {restaurant.subscriptionEndDate ? (
                                <>
                                  <span className={`px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium inline-flex items-center gap-1 ${
                                    subStatus.color === 'red' ? 'bg-red-100 text-red-800' :
                                    subStatus.color === 'orange' ? 'bg-orange-100 text-orange-800' :
                                    'bg-green-100 text-green-800'
                                  }`}>
                                    <Clock className="w-3 h-3" />
                                    {subStatus.text}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {new Date(restaurant.subscriptionEndDate).toLocaleDateString()}
                                  </span>
                                </>
                              ) : (<span className="px-2 md:px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-xs md:text-sm">
                                  Not Set
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 md:px-6 py-3 md:py-4">
                            <span className={`px-2 md:px-3 py-1 rounded-full text-xs md:text-sm font-medium ${
                              restaurant.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {restaurant.status}
                            </span>
                          </td>
                          <td className="px-4 md:px-6 py-3 md:py-4">
                            <div className="flex gap-1 md:gap-2">
                              <button
                                onClick={() => handleEdit(restaurant)}
                                disabled={saving}
                                className="p-1.5 md:p-2 text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50 transition"
                                title="Edit restaurant"
                              >
                                <Edit className="w-4 h-4 md:w-5 md:h-5" />
                              </button>
                              <button
                                onClick={() => handleDelete(restaurant.id)}
                                disabled={saving}
                                className="p-1.5 md:p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 transition"
                                title="Delete restaurant"
                              >
                                <Trash2 className="w-4 h-4 md:w-5 md:h-5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                        No restaurants match your search
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 overflow-y-auto">
          <div className="bg-white rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 md:px-8 py-4 md:py-6 flex justify-between items-center z-10">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800">
                {editingRestaurant ? 'Edit Restaurant' : 'Add Restaurant'}
              </h2>
              <button 
                onClick={closeModal}
                className="text-gray-500 hover:text-gray-700 p-1"
                disabled={saving}
              >
                <X className="w-5 h-5 md:w-6 md:h-6" />
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-4 md:p-8 space-y-6">
              {/* Basic Information Section */}
              <div>
                <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                  <Building2 className="w-4 h-4 md:w-5 md:h-5 text-orange-500" />
                  <span>Basic Information</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Restaurant Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      required
                      disabled={saving}
                      autoFocus
                      placeholder="Enter restaurant name"
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Username <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.username}
                      onChange={(e) => handleFormChange('username', e.target.value)}
                      className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      required
                      disabled={saving}
                      placeholder="Enter username"
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleFormChange('email', e.target.value)}
                        className={`w-full px-3 md:px-4 py-2 text-sm md:text-base border rounded-lg focus:ring-2 focus:ring-orange-500 ${
                          emailExists ? 'border-red-500' : 'border-gray-300'
                        }`}
                        required
                        disabled={saving || editingRestaurant}
                        placeholder="owner@restaurant.com"
                      />
                      {emailChecking && !editingRestaurant && (
                        <div className="absolute right-3 top-2.5">
                          <Loader className="w-4 h-4 text-orange-500 animate-spin" />
                        </div>
                      )}
                      {!emailChecking && emailCheckMessage && !editingRestaurant && (
                        <div className="absolute right-3 top-2.5">
                          {emailExists ? (
                            <AlertCircle className="w-4 h-4 text-red-500" />
                          ) : (
                            <CheckCircle className="w-4 h-4 text-green-500" />
                          )}
                        </div>
                      )}
                    </div>
                    {emailCheckMessage && !editingRestaurant && (
                      <p className={`text-xs mt-1 ${emailExists ? 'text-red-500' : 'text-green-600'}`}>
                        {emailCheckMessage}
                      </p>
                    )}
                    {editingRestaurant && (
                      <p className="text-xs text-gray-500 mt-1">Email cannot be changed after creation</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleFormChange('password', e.target.value)}
                      className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      required={!editingRestaurant}
                      disabled={saving || editingRestaurant}
                      placeholder={editingRestaurant ? "Password managed through Firebase Auth" : "Enter password (min 6 characters)"}
                      minLength="6"
                    />
                    {editingRestaurant && (
                      <p className="text-xs text-gray-500 mt-1">
                        Password cannot be changed here. User must use "Forgot Password" feature.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      GST Number <span className="text-gray-400">(Optional)</span>
                    </label>
                    <input
                      type="text"
                      value={formData.gstNo}
                      onChange={(e) => handleFormChange('gstNo', e.target.value)}
                      className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      disabled={saving}
                      placeholder="22AAAAA0000A1Z5"
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Package <span className="text-red-500">*</span>
                    </label>
                    {/* <select
                      value={formData.package}
                      onChange={(e) => handleFormChange('package', e.target.value)}
                      className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      required
                      disabled={saving}
                    >
                      <option>Basic</option>
                      <option>Premium</option>
                      <option>Advanced</option>
                    </select> */}

                    <select
  value={formData.package}
  onChange={(e) => handleFormChange('package', e.target.value)}
  className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
  required
  disabled={saving || availablePackages.length === 0}
>
  {availablePackages.length === 0 ? (
    <option value="">No packages available</option>
  ) : (
    availablePackages.map(pkg => (
      <option key={pkg.id} value={pkg.name}>
        {pkg.name} - ₹{pkg.price}/month ({pkg.qrLimit || 10} QR Tables)
      </option>
    ))
  )}
</select>
{availablePackages.length === 0 && (
  <p className="text-xs text-red-500 mt-1">
    Please create packages first before adding restaurants
  </p>
)}
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Status <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => handleFormChange('status', e.target.value)}
                      className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      required
                      disabled={saving}
                    >
                      <option>Active</option>
                      <option>Inactive</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Subscription Section */}
              <div>
                <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                  <Calendar className="w-4 h-4 md:w-5 md:h-5 text-orange-500" />
                  <span>Subscription Details</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Subscription Duration <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={formData.subscriptionMonths}
                      onChange={(e) => handleFormChange('subscriptionMonths', e.target.value)}
                      className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      required
                      disabled={saving}
                    >
                      <option value="1">1 Month</option>
                      <option value="3">3 Months</option>
                      <option value="6">6 Months</option>
                      <option value="12">1 Year</option>
                      <option value="24">2 Years</option>
                    </select>
                  </div>
                  {editingRestaurant && editingRestaurant.subscriptionEndDate && (
                    <div>
                      <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                        Current End Date
                      </label>
                      <div className="px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg bg-gray-50">
                        {new Date(editingRestaurant.subscriptionEndDate).toLocaleDateString()}
                        <span className="ml-2 text-xs text-gray-500">
                          ({SubscriptionService.getDaysRemaining(editingRestaurant.subscriptionEndDate)} days remaining)
                        </span>
                      </div>
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {editingRestaurant 
                    ? "Selecting duration will extend the subscription from today's date" 
                    : "Subscription will start from today's date"}
                </p>
              </div>

              {/* Contact Information Section */}
              <div>
                <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                  <Phone className="w-4 h-4 md:w-5 md:h-5 text-orange-500" />
                  <span>Contact Information</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4">
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Owner Phone Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      value={formData.ownerPhone}
                      onChange={(e) => handleFormChange('ownerPhone', e.target.value)}
                      className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      required
                      disabled={saving}
                      placeholder="+91 9876543210"
                      pattern="[0-9+\s()-]{10,15}"
                    />
                    <p className="text-xs text-gray-500 mt-1">10-15 digits required</p>
                  </div>
                </div>
              </div>

              {/* Location Information Section */}
              <div>
                <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-3 md:mb-4 flex items-center gap-2">
                  <MapPin className="w-4 h-4 md:w-5 md:h-5 text-orange-500" />
                  <span>Location Information</span>
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mb-3 md:mb-4">
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      City <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.city}
                      onChange={(e) => handleFormChange('city', e.target.value)}
                      className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      required
                      disabled={saving}
                      placeholder="Mumbai"
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      State <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.state}
                      onChange={(e) => handleFormChange('state', e.target.value)}
                      className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      required
                      disabled={saving}
                      placeholder="Maharashtra"
                    />
                  </div>
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Country <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.country}
                      onChange={(e) => handleFormChange('country', e.target.value)}
                      className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      required
                      disabled={saving}
                      placeholder="India"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-3 md:mb-4">
                  <div>
                    <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                      Pin Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.pinCode}
                      onChange={(e) => handleFormChange('pinCode', e.target.value)}
                      className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                      required
                      disabled={saving}
                      placeholder="400001"
                      pattern="[0-9]{5,6}"
                      maxLength="6"
                    />
                    <p className="text-xs text-gray-500 mt-1">5-6 digits required</p>
                  </div>
                </div>
                <div>
                  <label className="block text-xs md:text-sm font-medium text-gray-700 mb-1.5 md:mb-2">
                    Full Address <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={formData.address}
                    onChange={(e) => handleFormChange('address', e.target.value)}
                    className="w-full px-3 md:px-4 py-2 text-sm md:text-base border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    rows="3"
                    required
                    disabled={saving}
                    placeholder="Enter full address..."
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 md:px-6 py-2 md:py-3 text-sm md:text-base border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                  disabled={saving}
                >
                  Cancel
                </button>
                <button
                  type="submit"
            disabled={saving || (!editingRestaurant && emailExists) || availablePackages.length === 0}                  className="bg-orange-500 text-white px-4 md:px-6 py-2 md:py-3 rounded-lg font-semibold hover:bg-orange-600 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition text-sm md:text-base"
                >
                  {saving ? (
                    <>
                      <Loader className="w-4 h-4 md:w-5 md:h-5 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>{editingRestaurant ? 'Update Restaurant' : 'Add Restaurant'}</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminRestaurants;